import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users, Phone, Calendar, Home, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { dashboard, leads, viewings, calls } from '@/lib/api';
import { formatDateTime, formatTime, formatDuration, formatPhone, statusLabels, statusColors, cn } from '@/lib/utils';
import StatsLine from '@/components/StatsLine';

export default function Dashboard() {
  const { token } = useAuthStore();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboard.stats(token!),
    enabled: !!token,
  });

  const { data: recentLeads } = useQuery({
    queryKey: ['recent-leads'],
    queryFn: () => leads.list(token!, { limit: 5 }),
    enabled: !!token,
  });

  const { data: todayViewings } = useQuery({
    queryKey: ['today-viewings'],
    queryFn: () => viewings.today(token!),
    enabled: !!token,
  });

  const { data: todayCalls } = useQuery({
    queryKey: ['today-calls'],
    queryFn: () => calls.today(token!, 10),
    enabled: !!token,
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
      </div>
    );
  }

  const statsLineItems = [
    {
      label: 'Leads',
      value: stats?.leads.total || 0,
      icon: Users,
      accentClass: 'text-blue-600',
    },
    {
      label: 'Símtöl',
      value: stats?.calls.total || 0,
      icon: Phone,
      accentClass: 'text-green-600',
    },
    {
      label: 'Skoðanir',
      value: stats?.viewings.booked || 0,
      icon: Calendar,
      accentClass: 'text-purple-600',
    },
    {
      label: 'Eignir',
      value: stats?.properties.active || 0,
      icon: Home,
      accentClass: 'text-orange-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Góðan daginn! 👋</h1>
        <p className="text-gray-600 mt-1">
          Hér er yfirlit yfir starfsemina í dag.
        </p>
      </div>

      {/* Stats Line */}
      <StatsLine items={statsLineItems} />

      {/* Today's Calls - Full Width */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PhoneIncoming className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900">Símtöl í dag</h2>
            {todayCalls && (
              <span className="ml-2 px-2 py-0.5 text-sm font-medium bg-green-100 text-green-700 rounded-full">
                {todayCalls.pagination.total}
              </span>
            )}
          </div>
          <Link to="/calls" className="text-sm text-prima-600 hover:text-prima-700">
            Sjá öll símtöl →
          </Link>
        </div>
        <div className="divide-y divide-gray-200">
          {(!todayCalls || todayCalls.data.length === 0) && (
            <div className="px-6 py-8 text-center">
              <Phone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Engin símtöl í dag</p>
              <p className="text-sm text-gray-400 mt-1">Símtöl birtast hér þegar þau berast</p>
            </div>
          )}
          {todayCalls?.data.map((call) => (
            <Link
              key={call.id}
              to={`/calls/${call.id}`}
              className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className={cn(
                  'p-2 rounded-full',
                  call.ai_handled ? 'bg-purple-100' : 'bg-gray-100'
                )}>
                  {call.transferred_to ? (
                    <PhoneOutgoing className="w-4 h-4 text-blue-600" />
                  ) : (
                    <PhoneIncoming className="w-4 h-4 text-green-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {formatPhone(call.caller_phone)}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{formatTime(call.created_at)}</span>
                    <span>•</span>
                    <span>{formatDuration(call.duration_seconds)}</span>
                    {call.ai_handled && (
                      <>
                        <span>•</span>
                        <span className="text-purple-600">AI</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className={cn(statusColors[call.outcome] || 'badge-gray')}>
                  {statusLabels[call.outcome] || call.outcome}
                </span>
                {call.transferred_to && (
                  <p className="text-xs text-gray-400 mt-1">
                    Tengt: {call.transferred_to}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Nýjustu leads</h2>
            <Link to="/leads" className="text-sm text-prima-600 hover:text-prima-700">
              Sjá allt →
            </Link>
          </div>
          <div className="divide-y divide-gray-200">
            {recentLeads?.data.length === 0 && (
              <p className="px-6 py-8 text-center text-gray-500">
                Engin leads ennþá
              </p>
            )}
            {recentLeads?.data.map((lead) => (
              <Link
                key={lead.id}
                to={`/leads/${lead.id}`}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {lead.caller_name || lead.caller_phone}
                  </p>
                  <p className="text-sm text-gray-500">
                    {lead.property_address || 'Engin eign tengd'}
                  </p>
                </div>
                <div className="text-right">
                  <span className={statusColors[lead.status] || 'badge-gray'}>
                    {statusLabels[lead.status] || lead.status}
                  </span>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDateTime(lead.created_at)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Today's Viewings */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Skoðanir í dag</h2>
            <Link to="/viewings" className="text-sm text-prima-600 hover:text-prima-700">
              Sjá allt →
            </Link>
          </div>
          <div className="divide-y divide-gray-200">
            {(!todayViewings || todayViewings.length === 0) && (
              <p className="px-6 py-8 text-center text-gray-500">
                Engar skoðanir í dag
              </p>
            )}
            {todayViewings?.map((viewing) => (
              <div
                key={viewing.id}
                className="px-6 py-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {viewing.property_address}
                  </p>
                  <p className="text-sm text-gray-500">
                    {viewing.lead_name} • {viewing.agent_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-prima-600">
                    {formatDateTime(viewing.scheduled_at).split(' ').slice(-1)[0]}
                  </p>
                  <p className="text-xs text-gray-400">
                    {viewing.duration_minutes} mín
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
